import { useEffect, useState, useCallback } from "react";
import { MapPin, Clock, Phone, User, DollarSign, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/pages/OwnerDashboard";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";

const NEXT_STATUS = {
  booked: { next: "on_the_way", label: "Start — On the way" },
  on_the_way: { next: "in_progress", label: "Arrived — Start job" },
  in_progress: { next: "completed", label: "Mark completed" },
};

export default function EmployeeJobs() {
  const [bookings, setBookings] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/bookings");
      setBookings(res.data.bookings);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });

  const confirmSchedule = async (bookingId, date, time_slot) => {
    try {
      await api.patch(`/bookings/${bookingId}/schedule`, { date, time_slot });
      toast.success("Date confirmed — customer notified by email");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const advance = async (b) => {
    const next = NEXT_STATUS[b.status]?.next;
    if (!next) return;
    try {
      await api.patch(`/bookings/${b.id}/status`, { status: next });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const upcoming = bookings.filter((b) => b.status !== "completed");
  const done = bookings.filter((b) => b.status === "completed");

  const JobCard = ({ b }) => (
    <Card className="rounded-2xl border-border" data-testid={`myjob-card-${b.ref}`}>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-bold text-lg">{b.service_name}</span>
              <StatusBadge status={b.status} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">{b.ref}</p>
          </div>
          <p className="font-display font-bold text-lg flex items-center gap-1"><DollarSign className="h-4 w-4" />{b.final_price ?? b.customer_estimate ?? b.estimated_price}</p>
        </div>
        <div className="space-y-2 text-sm mb-4">
          <p className="flex items-center gap-2"><User className="h-4 w-4 text-primary shrink-0" /> {b.name}</p>
          <p className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-primary shrink-0" />
            <a href={`tel:${b.phone.replace(/\s/g, "")}`} className="text-primary font-medium" data-testid={`myjob-call-${b.ref}`}>{b.phone}</a>
          </p>
          <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary shrink-0" /> {b.address}</p>
          {b.date ? (
            <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary shrink-0" /> {b.date} &middot; {b.time_slot}</p>
          ) : (
            <div className="bg-harvest/15 border border-harvest/40 rounded-xl p-3" data-testid={`myjob-schedule-form-${b.ref}`}>
              <p className="text-xs font-bold text-yellow-900 uppercase tracking-wide mb-2">Date not set — confirm with the customer</p>
              <form onSubmit={(e) => { e.preventDefault(); const fd = new FormData(e.currentTarget); confirmSchedule(b.id, fd.get("date"), fd.get("time")); }} className="flex flex-wrap items-center gap-2">
                <input name="date" type="date" required min={todayStr} className="h-10 rounded-xl border border-input bg-white px-3 text-sm" data-testid={`myjob-schedule-date-${b.ref}`} />
                <select name="time" required defaultValue="" className="h-10 rounded-xl border border-input bg-white px-3 text-sm" data-testid={`myjob-schedule-time-${b.ref}`}>
                  <option value="" disabled>Time...</option>
                  {["8:00 AM", "10:00 AM", "12:00 PM", "2:00 PM", "4:00 PM", "Flexible"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <button type="submit" className="h-10 px-4 rounded-xl bg-primary hover:bg-[#21421D] text-white text-sm font-semibold transition-colors" data-testid={`myjob-schedule-save-${b.ref}`}>Confirm</button>
              </form>
            </div>
          )}
          {b.details && (
            <p className="flex items-start gap-2 bg-secondary rounded-lg p-3">
              <StickyNote className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {b.details}
            </p>
          )}
        </div>
        {b.photos?.length > 0 && (
          <div className="flex gap-3 mb-4 flex-wrap">
            {b.photos.map((p, i) => (
              <Dialog key={i}>
                <DialogTrigger asChild>
                  <button className="h-16 w-16 rounded-xl overflow-hidden border border-border" data-testid={`myjob-photo-${b.ref}-${i}`}>
                    <img src={p} alt={`Job ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl max-w-2xl">
                  <img src={p} alt={`Job ${i + 1}`} className="w-full rounded-xl" />
                </DialogContent>
              </Dialog>
            ))}
          </div>
        )}
        {NEXT_STATUS[b.status] && (
          <button onClick={() => advance(b)} data-testid={`myjob-advance-${b.ref}`}
            className="w-full min-h-[48px] rounded-xl bg-primary hover:bg-[#21421D] text-white font-semibold text-sm transition-colors">
            {NEXT_STATUS[b.status].label}
          </button>
        )}
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout title="My jobs">
      <div className="max-w-2xl space-y-8" data-testid="employee-jobs-page">
        <section>
          <h2 className="text-lg font-medium mb-4">Upcoming ({upcoming.length})</h2>
          <div className="space-y-4" data-testid="employee-upcoming-list">
            {upcoming.length === 0 && (
              <Card className="rounded-2xl border-border"><CardContent className="p-8 text-sm text-muted-foreground" data-testid="employee-jobs-empty">No jobs assigned to you yet.</CardContent></Card>
            )}
            {upcoming.map((b) => <JobCard key={b.id} b={b} />)}
          </div>
        </section>
        {done.length > 0 && (
          <section>
            <h2 className="text-lg font-medium mb-4">Completed ({done.length})</h2>
            <div className="space-y-4" data-testid="employee-completed-list">
              {done.map((b) => <JobCard key={b.id} b={b} />)}
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  );
}
